import React, { useEffect, useState } from 'react';
import HojaMovil from './HojaMovil';

// Hoja "Editar línea" de la ficha de presupuesto en el móvil: personas,
// precio por persona e IVA con campos grandes (16 px, sin zoom de iOS) y un
// botón Guardar. No guarda nada por su cuenta: `onGuardar(cambios)` recibe
// solo lo que ha cambiado ({ uds, precio, iva }) y la ficha lo guarda con las
// mismas funciones que la edición en línea del escritorio.
// `children`: el horario de la línea (los mismos controles que en la fila).

const CLASE_CAMPO = 'w-full h-11 px-3 border border-[#CBD3CF] rounded-lg text-base text-[#16221D] bg-white tabular-nums focus:outline-none focus:ring-2 focus:ring-duetto-primary disabled:bg-[#F4F6F4] disabled:text-[#5B6B64]';

const aTexto = (v) => (v === null || v === undefined || Number.isNaN(Number(v)) ? '' : String(v).replace('.', ','));
const aNumero = (t) => {
  const n = parseFloat(String(t ?? '').replace(/\s/g, '').replace(',', '.'));
  return Number.isFinite(n) ? n : null;
};

export default function HojaEditarLineaMovil({
  abierta,
  onCerrar,
  titulo,
  subtitulo = null,
  valores = {},
  editables = {},
  notaUds = null,
  totalTexto = null,
  onGuardar,
  children = null,
  testid = 'hoja-editar-linea',
}) {
  const [uds, setUds] = useState('');
  const [precio, setPrecio] = useState('');
  const [iva, setIva] = useState(10);
  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState(null);

  // Al abrir (o cambiar de línea) se cargan los valores actuales
  useEffect(() => {
    if (!abierta) return;
    setUds(aTexto(valores.uds));
    setPrecio(aTexto(valores.precio));
    setIva(Number(valores.iva) || 10);
    setError(null);
    setGuardando(false);
  }, [abierta, titulo, valores.uds, valores.precio, valores.iva]);

  const hayCampos = editables.uds || editables.precio || editables.iva;

  const guardar = async () => {
    const cambios = {};
    if (editables.uds) {
      const n = parseInt(String(uds).trim(), 10);
      if (!Number.isFinite(n) || n <= 0) {
        setError('Pon un número de personas mayor que 0.');
        return;
      }
      if (n !== Number(valores.uds)) cambios.uds = n;
    }
    if (editables.precio) {
      const p = aNumero(precio);
      if (p === null || p < 0) {
        setError('Pon un precio válido (por ejemplo 38,50).');
        return;
      }
      if (Math.abs(p - (Number(valores.precio) || 0)) > 0.0001) cambios.precio = p;
    }
    if (editables.iva && Number(iva) !== Number(valores.iva)) cambios.iva = Number(iva);
    if (Object.keys(cambios).length === 0) {
      onCerrar();
      return;
    }
    setGuardando(true);
    setError(null);
    try {
      await onGuardar(cambios);
      onCerrar();
    } catch (e) {
      setError(e?.message || 'No se ha podido guardar.');
    } finally {
      setGuardando(false);
    }
  };

  return (
    <HojaMovil
      abierta={abierta}
      onCerrar={onCerrar}
      titulo={titulo}
      subtitulo={subtitulo}
      clave="ficha-hoja-editar-linea"
      testid={testid}
      pie={hayCampos ? (
        <div className="flex gap-2">
          <button
            type="button"
            onClick={onCerrar}
            className="h-11 px-4 rounded-lg border border-[#CBD3CF] bg-white text-[15px] font-semibold text-[#16221D]"
          >
            Cancelar
          </button>
          <button
            type="button"
            onClick={guardar}
            disabled={guardando}
            className="h-11 flex-1 rounded-lg bg-duetto-primary text-white text-[15px] font-bold disabled:opacity-60"
            data-testid={`${testid}-guardar`}
          >
            {guardando ? 'Guardando...' : 'Guardar'}
          </button>
        </div>
      ) : null}
    >
      <div className="px-5 py-4 space-y-4">
        {hayCampos && (
          <div className="grid grid-cols-2 gap-3">
            <label className="block">
              <span className="block text-[13px] font-semibold text-[#5B6B64] mb-1">Personas</span>
              <input
                type="text"
                inputMode="numeric"
                pattern="[0-9]*"
                value={uds}
                onChange={(e) => { setUds(e.target.value); setError(null); }}
                disabled={!editables.uds || guardando}
                className={CLASE_CAMPO}
                data-testid={`${testid}-uds`}
              />
            </label>
            <label className="block">
              <span className="block text-[13px] font-semibold text-[#5B6B64] mb-1">Precio por persona (€)</span>
              <input
                type="text"
                inputMode="decimal"
                value={precio}
                onChange={(e) => { setPrecio(e.target.value); setError(null); }}
                disabled={!editables.precio || guardando}
                className={CLASE_CAMPO}
                data-testid={`${testid}-precio`}
              />
            </label>
            {notaUds && <p className="col-span-2 -mt-1 text-[13px] text-[#5B6B64]">{notaUds}</p>}
            <label className="block col-span-2">
              <span className="block text-[13px] font-semibold text-[#5B6B64] mb-1">IVA</span>
              <select
                value={iva}
                onChange={(e) => setIva(parseInt(e.target.value, 10))}
                disabled={!editables.iva || guardando}
                className={CLASE_CAMPO}
                data-testid={`${testid}-iva`}
              >
                <option value={10}>10 %</option>
                <option value={21}>21 %</option>
              </select>
            </label>
          </div>
        )}
        {error && <p className="text-sm font-semibold text-[#8A1C12]" role="alert">{error}</p>}
        {totalTexto && <p className="text-sm text-[#5B6B64]">{totalTexto}</p>}
        {children}
      </div>
    </HojaMovil>
  );
}
